import React, { useEffect, useRef } from 'react';
import { Modal, View, Animated, PanResponder, TouchableWithoutFeedback } from 'react-native';
import Stack from '../../../../v2BaseComponents/Stack';
import Text from '../../../../v2BaseComponents/Text';
import IconButton from '../../../../v2BaseComponents/IconButton';
import { APITypes } from '../../../../../primary-record-types';
import getName from '../../../../../utils/getName';
import getDateString from '../../../../../utils/getDateString';
import { getIntl } from '../../../../../utils/intl';
import { useIsMobileLayout } from '../../../../../hooks/useIsMobileLayout';
import { getFriendlyContentType } from '../../../../../utils/sharedMessages';

const intl = getIntl('file-details-modal', {
    fileProperties: 'File Properties',
    uploadedAt: 'Uploaded At',
    uploadedBy: 'Uploaded By',
    contentType: 'Content Type',
    userNoLongerExists: 'User no longer exists',
});

export type FileDetailsModalProps = {
    file: APITypes.medicalProfileFilesService.MedicalProfileFileListItem;
    fileExtension: string;
    isVisible: boolean;
    onClose: () => void;
};

const BOTTOM_SHEET_HEIGHT = 250;

const FileDetailsModal: React.FC<FileDetailsModalProps> = ({ file, fileExtension, isVisible, onClose }) => {
    const isMobile = useIsMobileLayout();

    // Bottom sheet animation refs
    const translateY = useRef(new Animated.Value(BOTTOM_SHEET_HEIGHT)).current;
    const opacity = useRef(new Animated.Value(0)).current;

    // Bottom sheet animations
    const showBottomSheet = () => {
        Animated.parallel([
            Animated.timing(translateY, {
                toValue: 0,
                duration: 300,
                useNativeDriver: true,
            }),
            Animated.timing(opacity, {
                toValue: 1,
                duration: 300,
                useNativeDriver: true,
            }),
        ]).start();
    };

    const hideBottomSheet = () => {
        Animated.parallel([
            Animated.timing(translateY, {
                toValue: BOTTOM_SHEET_HEIGHT,
                duration: 250,
                useNativeDriver: true,
            }),
            Animated.timing(opacity, {
                toValue: 0,
                duration: 250,
                useNativeDriver: true,
            }),
        ]).start(() => {
            onClose();
        });
    };

    // Pan responder for swipe gestures (mobile only)
    const panResponder = PanResponder.create({
        onStartShouldSetPanResponder: () => isMobile,
        onMoveShouldSetPanResponder: (_, gestureState) => {
            return isMobile && Math.abs(gestureState.dy) > 5;
        },
        onPanResponderMove: (_, gestureState) => {
            if (gestureState.dy > 0) {
                translateY.setValue(gestureState.dy);
            }
        },
        onPanResponderRelease: (_, gestureState) => {
            if (gestureState.dy > 50 || gestureState.vy > 0.5) {
                hideBottomSheet();
            } else {
                Animated.spring(translateY, {
                    toValue: 0,
                    useNativeDriver: true,
                }).start();
            }
        },
    });

    const handleClose = isMobile ? hideBottomSheet : onClose;

    // Handle animations for mobile
    useEffect(() => {
        if (isMobile) {
            if (isVisible) {
                showBottomSheet();
            } else {
                translateY.setValue(BOTTOM_SHEET_HEIGHT);
                opacity.setValue(0);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isVisible, isMobile]);

    // Shared content component
    const Content = () => (
        <>
            {/* Header with title and close button */}
            <Stack horizontal fullWidth justify="space-between" align="center" style={{ marginBottom: 20 }}>
                <Text variant="h4">{intl('fileProperties')}</Text>
                <IconButton icon="X" variant="minimal" onPress={handleClose} customSize="chat-button-icon" />
            </Stack>

            {/* Details */}
            <Stack vertical fullWidth space={16}>
                <DetailRow label={intl('uploadedAt')} value={getDateString(file.dateAdded, 'date-time')} />
                <DetailRow
                    label={intl('uploadedBy')}
                    value={file.uploadUser ? getName(file.uploadUser) : intl('userNoLongerExists')}
                />
                <DetailRow
                    label={intl('contentType')}
                    value={getFriendlyContentType(fileExtension, file.contentType)}
                />
            </Stack>
        </>
    );

    if (!isVisible) {
        return null;
    }

    return (
        <Modal visible={isVisible} transparent animationType={isMobile ? 'none' : 'fade'} onRequestClose={handleClose}>
            {isMobile ? (
                // Mobile: Bottom Sheet
                <TouchableWithoutFeedback onPress={hideBottomSheet}>
                    <Animated.View
                        style={{
                            flex: 1,
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            opacity: opacity,
                        }}
                    >
                        <TouchableWithoutFeedback>
                            <Animated.View
                                style={{
                                    position: 'absolute',
                                    bottom: 0,
                                    left: 0,
                                    right: 0,
                                    height: BOTTOM_SHEET_HEIGHT,
                                    backgroundColor: 'white',
                                    borderTopLeftRadius: 20,
                                    borderTopRightRadius: 20,
                                    transform: [{ translateY }],
                                    shadowColor: '#000',
                                    shadowOffset: {
                                        width: 0,
                                        height: -2,
                                    },
                                    shadowOpacity: 0.25,
                                    shadowRadius: 3.84,
                                    elevation: 5,
                                }}
                                {...panResponder.panHandlers}
                            >
                                {/* Drag Handle */}
                                <View
                                    style={{
                                        width: 40,
                                        height: 4,
                                        backgroundColor: '#DDD',
                                        borderRadius: 2,
                                        alignSelf: 'center',
                                        marginTop: 8,
                                        marginBottom: 16,
                                    }}
                                />

                                <View style={{ paddingHorizontal: 20, flex: 1 }}>
                                    <Content />
                                </View>
                            </Animated.View>
                        </TouchableWithoutFeedback>
                    </Animated.View>
                </TouchableWithoutFeedback>
            ) : (
                // Web: Modal
                <View
                    style={{
                        flex: 1,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        justifyContent: 'center',
                        alignItems: 'center',
                        paddingHorizontal: 20,
                    }}
                >
                    <TouchableWithoutFeedback onPress={onClose}>
                        <View style={{ flex: 1, width: '100%' }} />
                    </TouchableWithoutFeedback>

                    <View
                        style={{
                            backgroundColor: 'white',
                            borderRadius: 12,
                            padding: 16,
                            width: '90%',
                            maxWidth: 360,
                            shadowColor: '#000',
                            shadowOffset: {
                                width: 0,
                                height: 2,
                            },
                            shadowOpacity: 0.25,
                            shadowRadius: 3.84,
                            elevation: 5,
                        }}
                    >
                        <Content />
                    </View>

                    <TouchableWithoutFeedback onPress={onClose}>
                        <View style={{ flex: 1, width: '100%' }} />
                    </TouchableWithoutFeedback>
                </View>
            )}
        </Modal>
    );
};

type DetailRowProps = {
    label: string;
    value: string;
};

const DetailRow: React.FC<DetailRowProps> = ({ label, value }) => (
    <Stack horizontal fullWidth justify="space-between" align="center">
        <Text variant="body-1" style={{ color: '#666' }}>
            {label}
        </Text>
        <Text variant="body-1" style={{ fontWeight: '500' }}>
            {value}
        </Text>
    </Stack>
);

export default FileDetailsModal;
